import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabaseService from '../services/supabaseService';
import cloudinaryService from '../services/cloudinaryService';
import { FiUpload, FiCalendar, FiCreditCard, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const ReceiptUploadScreen = () => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Set current month as default value
    const now = new Date();
    setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  }, []);

  const pickImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setReceiptImage(file);
      }
    };
    
    input.click();
  };

  const selectMonth = () => {
    const input = document.createElement('input');
    input.type = 'month';
    
    if (selectedMonth) {
      const year = selectedMonth.getFullYear();
      const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
      input.value = `${year}-${month}`;
    }
    
    input.onchange = (e) => {
      if (e.target.value) {
        const [year, month] = e.target.value.split('-');
        setSelectedMonth(new Date(parseInt(year), parseInt(month) - 1, 1));
      }
    };
    
    input.click();
  };

  const uploadReceipt = async () => {
    if (!receiptImage || !selectedMonth) {
      toast.error('يرجى اختيار صورة الإيصال والشهر');
      return;
    }

    setUploading(true);
    
    try {
      const providerId = supabaseService.currentUser.id;
      
      // Upload image to Cloudinary
      const receiptUrl = await cloudinaryService.uploadImage(receiptImage, 'receipt');
      
      if (receiptUrl) {
        // Update URL in database
        await supabaseService.updateReceiptUrl(providerId, selectedMonth, receiptUrl);
        
        toast.success('تم رفع الإيصال بنجاح!');
        navigate('/provider');
      }
    } catch (e) {
      toast.error(`فشل رفع الإيصال: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const formatMonthDisplay = () => {
    if (!selectedMonth) return 'اختر الشهر';
    
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth() + 1;
    return `${year} / ${month.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - ثابت */}
      <div className="bg-khidma-black text-white px-4 md:px-6 py-5 shadow-md flex items-center justify-between">
        <button
          onClick={() => navigate('/provider')}
          className="text-white hover:text-gray-300 transition-colors p-2 -ml-2 rounded-full hover:bg-white/10"
        >
          <FiArrowLeft className="text-xl md:text-2xl" />
        </button>
        <h1 className="text-xl md:text-2xl font-bold">رفع وصل الإيداع</h1>
        <div className="w-8 md:w-10"></div> {/* Placeholder for centering balance */}
      </div>
      
      {/* Main Content */}
      <div className="flex-1 w-full flex items-center justify-center p-4 md:p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center lg:items-start">
          
          {/* اليسار: النموذج (على الحاسوب) / المحتوى الرئيسي (على الهاتف) */}
          <div className="order-1 lg:order-1 space-y-6 w-full">
            
            {/* Month Selection */}
            <div>
              <label className="block text-gray-900 font-bold mb-2 text-sm md:text-base">اختر شهر الإيداع</label>
              <button
                onClick={selectMonth}
                className="w-full p-3 bg-white border border-gray-300 rounded-lg flex items-center justify-between hover:border-gray-900 hover:bg-gray-50 transition-all shadow-sm"
              >
                <span className="text-gray-800 font-medium">{formatMonthDisplay()}</span>
                <FiCalendar className="text-gray-500" />
              </button>
            </div>
            
            {/* Receipt Image Upload */}
            <div>
              <label className="block text-gray-900 font-bold mb-2 text-sm md:text-base">ارفع صورة الإيصال</label>
              <div
                onClick={pickImage}
                className="h-48 md:h-64 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-white hover:border-gray-400 transition-all bg-white"
              >
                {receiptImage ? (
                  <img
                    src={URL.createObjectURL(receiptImage)}
                    alt="Receipt"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <>
                    <div className="bg-gray-100 rounded-full p-4 mb-3">
                      <FiUpload className="text-3xl text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">اضغط هنا لاختيار صورة</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, PDF</p>
                  </>
                )}
              </div>
            </div>
            
            {/* Upload Button */}
            <button
              onClick={uploadReceipt}
              disabled={uploading}
              className="w-full bg-khidma-black text-white py-3 rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base shadow-md"
            >
              {uploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
              ) : (
                'رفع الإيصال'
              )}
            </button>
          </div>
          
          {/* اليمين: معلومات البنك (على الحاسوب) */}
          <div className="order-2 lg:order-2 w-full">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-300 lg:bg-white lg:border-gray-100 lg:rounded-2xl lg:p-8 lg:shadow-xl lg:border-0">
              <div className="flex items-center mb-4 lg:mb-6">
                <div className="bg-white rounded-lg p-3 mr-3 lg:bg-gray-100 lg:mr-4">
                  <FiCreditCard className="text-gray-900 text-xl lg:text-3xl" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg lg:text-xl">معلومات الدفع</h3>
                  <p className="text-gray-600 text-sm lg:text-base">قم بالتحويل إلى الحساب التالي</p>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-100 lg:bg-gray-50 lg:border-gray-200 lg:mb-6 shadow-sm">
                <p className="font-bold text-gray-900 mb-1">البنك: RIP</p>
                <p className="text-gray-800 mb-1 text-sm md:text-base">رقم الحساب: 00799999002916235172</p>
                
              </div>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText('00799999002916235172');
                  toast.success('تم نسخ رقم الحساب');
                }}
                className="w-full bg-khidma-black text-white py-3 rounded-lg font-bold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 lg:py-4 shadow-md lg:shadow-none"
              >
                <FiUpload className="text-lg" />
                نسخ رقم الحساب
              </button>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default ReceiptUploadScreen;